import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { PermissionsHttpClient } from '@omnea-app/permissions/permissionsHttpClient'
import { useAuthzContext } from './AuthzContextProvider'
import { isNotFoundBody } from './isNotFoundBody'
import { recordAccessQueryKey } from './recordAccessQuery'
import { useRoleAccessContext } from './useRoleAccessContext'
import type { RoleAccessContext } from './roleAccess'
import type { PermissionSetEntity, RecordPermissionsFor } from '@omnea-app/authz'

// 20 minutes. Freshness comes from a mutation invalidating the prefix, not from expiry.
const ACCESS_FRESHNESS_MS = 20 * 60 * 1000

/** The states the record gate lets a page render under. */
export type SettledRecordAccess<Entity extends PermissionSetEntity> =
  // The organisation is not on the permissions service, so the role rule decides.
  | { entity: Entity; status: 'role'; roleContext: RoleAccessContext }
  | { entity: Entity; status: 'resolved'; permissions: RecordPermissionsFor<Entity> }

export type RecordAccess<Entity extends PermissionSetEntity> =
  | { entity: Entity; status: 'loading' }
  // The check failed, so nothing is permitted.
  | { entity: Entity; status: 'unavailable'; error: unknown }
  | SettledRecordAccess<Entity>

export type AnyRecordAccess = RecordAccess<PermissionSetEntity>

type RecordPermissionsAnswer<Entity extends PermissionSetEntity> = { entity: Entity; permissions: RecordPermissionsFor<Entity> }

/**
 * What the service answered, which outlives the render that asked. The role context is deliberately
 * absent: it changes with the session, so a cached copy would decide on a stale one.
 */
type CachedRecordAnswer<Entity extends PermissionSetEntity> =
  | { entity: Entity; status: 'role' }
  | { entity: Entity; status: 'resolved'; permissions: RecordPermissionsFor<Entity> }

const answersFor = <Entity extends PermissionSetEntity>(
  answer: RecordPermissionsAnswer<PermissionSetEntity>,
  entity: Entity
): answer is RecordPermissionsAnswer<Entity> => answer.entity === entity

export const useRecordAccess = <Entity extends PermissionSetEntity>(entity: Entity, recordId: string): RecordAccess<Entity> => {
  const { getToken, user } = useAuthenticatedUser()
  const authzContext = useAuthzContext()
  const roleContext = useRoleAccessContext()

  const { data, isError, error } = useQuery({
    queryKey: recordAccessQueryKey(user.id, entity, recordId),
    queryFn: async (): Promise<CachedRecordAnswer<Entity>> => {
      const permissionsClient = new PermissionsHttpClient({ authorization: await getToken() })
      const { data: response, error } = await permissionsClient.GET('/entities/{entity}/{recordId}/permissions', {
        params: { path: { entity, recordId } },
      })
      if (isNotFoundBody(error)) return { entity, status: 'role' }
      throwIfApiError(error)
      if (response == null) throw new Error(`record permissions request for ${entity} returned no body`)

      const { recordPermissions } = response
      if (!answersFor(recordPermissions, entity)) {
        throw new Error(`record permissions request for ${entity} answered for ${recordPermissions.entity}`)
      }
      return { entity, status: 'resolved', permissions: recordPermissions.permissions }
    },
    enabled: authzContext.status === 'resolved',
    refetchOnWindowFocus: true,
    staleTime: ACCESS_FRESHNESS_MS,
    gcTime: ACCESS_FRESHNESS_MS,
  })

  return useMemo((): RecordAccess<Entity> => {
    // A context that has not answered is not an organisation off the service: treating it as one shows every
    // affordance, then withdraws some when the answer lands.
    if (authzContext.status === 'loading') return { entity, status: 'loading' }
    if (authzContext.status === 'unavailable') return { entity, status: 'unavailable', error: authzContext.error }
    if (authzContext.status === 'role') return { entity, status: 'role', roleContext }
    // Read before isError so a failed background refetch keeps the last-known answer rather than
    // collapsing every affordance mid-session.
    if (data != null) return data.status === 'role' ? { entity, status: 'role', roleContext } : data
    if (isError) return { entity, status: 'unavailable', error }
    return { entity, status: 'loading' }
  }, [entity, authzContext, roleContext, data, isError, error])
}
